import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DateRangePicker, { DateRange, defaultRange } from './DateRangePicker';
import TimeSeriesChart from './TimeSeriesChart';
import VolumeCards, { fmtExact } from './VolumeCards';

/**
 * /reports/legs/:id — one delivery destination.
 *
 * Deliberately the SIMPLEST drilldown in the Reporting tab. The body
 * drilldown has to show a Brain/Spine/Legs breakdown because a body is a
 * pipeline with many decision points; a delivery destination sits at the
 * END of exactly one body's pipeline and makes exactly one decision, so
 * there is nothing to break down. Volume over time plus its own four cards
 * is the whole story, and adding a node table here would be inventing
 * structure that does not exist.
 *
 * ACCEPT RATE, NOT DELIVERY RATE. At this level the number is
 * delivered ÷ (delivered + failed) — what this destination took divided by
 * what reached it. It is a true percentage and cannot exceed 100%. The
 * fan-out "delivery rate" on the body tables is a different measurement
 * that routinely does; the two must not be read as the same figure.
 *
 * A DELETED destination still resolves here. Its rollup rows and its
 * client assignment both outlive the node (migration 031), so the page
 * renders with a "deleted" marker rather than 404ing — otherwise a client's
 * own history would contain dead links.
 */

export default function ReportingLegPage() {
  const { id } = useParams<{ id: string }>();
  const { organization } = useAuth();
  const tz = organization?.timezone || undefined;

  const [range, setRange] = useState<DateRange>(() => defaultRange(tz));
  const [data, setData] = useState<api.ReportingLegResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { from, to } = range;

  const load = useCallback(async (forceFresh = false) => {
    if (!id) {return;}
    setLoading(true);
    setError(null);
    try {
      setData(await api.getReportingLeg(id, from, to, forceFresh));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load destination');
    } finally {
      setLoading(false);
    }
  }, [id, from, to]);

  useEffect(() => { void load(false); }, [load]);

  const leg = data?.leg;

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1280, margin: '0 auto' }}>
      <Link
        to="/reports"
        style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
      >
        ← Back to reporting
      </Link>

      <h1 style={{ margin: '10px 0 4px 0', fontSize: 28, fontWeight: 700, color: '#111827' }}>
        {leg?.nodeName || leg?.nodeType || 'Destination'}
        {leg?.detached && (
          <span style={{
            marginLeft: 10,
            fontSize: 12,
            fontWeight: 600,
            color: '#92400e',
            background: '#fffbeb',
            padding: '3px 8px',
            borderRadius: 999,
            verticalAlign: 'middle',
          }}>
            Deleted — history kept
          </span>
        )}
      </h1>

      {leg && (
        <p style={{ marginTop: 0, marginBottom: 20, color: '#6b7280', fontSize: 14 }}>
          {leg.clientId !== api.UNASSIGNED_CLIENT ? (
            <>
              Client:{' '}
              <Link to={`/reports/clients/${leg.clientId}`} style={inlineLink}>
                {leg.clientName || '(unnamed client)'}
              </Link>
            </>
          ) : (
            <>Client: <span style={{ color: '#9ca3af' }}>Unassigned</span></>
          )}
          {leg.dataBodyId && leg.dataBodyName && (
            <>
              {' · '}Data body:{' '}
              <Link to={`/reports/bodies/${leg.dataBodyId}`} style={inlineLink}>
                {leg.dataBodyName}
              </Link>
            </>
          )}
          {leg.legName && <>{' · '}Leg: {leg.legName}</>}
        </p>
      )}

      <div style={{ marginBottom: 20 }}>
        <DateRangePicker
          value={range}
          onChange={setRange}
          onRefresh={() => load(true)}
          timezone={tz}
        />
      </div>

      {error && (
        <div style={{
          padding: 16,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          color: '#991b1b',
          marginBottom: 20,
        }}>
          {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      )}

      {leg && data && (
        <>
          <div style={{ marginBottom: 24 }}>
            <VolumeCards
              volume={{
                today: leg.today,
                range: leg.range,
                gross: leg.gross,
                deliveredInRange: leg.range,
                deliveryRate: leg.acceptRate,
              }}
              preset={range.preset}
              rateLabel="Accept Rate"
              rateVariant="accept"
            />
          </div>

          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: 16,
            fontWeight: 600,
            color: '#374151',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}>
            Leg volume over time
          </h2>
          <TimeSeriesChart data={data.series} tz={tz} />

          <p style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>
            {fmtExact(leg.range)} delivered and {fmtExact(leg.failedInRange)} failed in the selected
            range. Accept rate is this destination&apos;s own — what it took divided by what reached
            it — so unlike a data body&apos;s delivery rate it never exceeds 100%.
          </p>
        </>
      )}
    </div>
  );
}

const inlineLink: React.CSSProperties = {
  color: '#2563eb',
  fontWeight: 600,
  textDecoration: 'none',
};
